import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import {
  BookingRequest,
  type Attribution,
  type BookingTopic,
} from "@vb/rgs-contract";
import { Seo } from "../lib/seo.js";
import { Section, SectionHeading } from "../components/Section.js";

/** When VITE_API_HOST is set we hit the absolute URL directly. Otherwise we
 *  let Vercel rewrite /public/* to the game-server. Same shape as Sandbox.tsx. */
const API_HOST_RAW: string | undefined = (
  import.meta as unknown as { env: { VITE_API_HOST?: string } }
).env.VITE_API_HOST;
const API_HOST: string = (API_HOST_RAW ?? "").replace(/\/$/, "");
const BOOKING_ENDPOINT: string = API_HOST_RAW
  ? `${API_HOST}/public/booking`
  : "/public/booking";

const TOPIC_OPTIONS: { value: BookingTopic; label: string }[] = [
  { value: "sandbox-walkthrough", label: "Sandbox walkthrough" },
  { value: "integration-deep-dive", label: "Integration deep-dive" },
  { value: "pricing", label: "Pricing & revenue share" },
  { value: "compliance", label: "Compliance & licensing" },
  { value: "other", label: "Other" },
];

/** Top 35 iGaming jurisdictions — kept in sync with Contact.tsx. We duplicate
 *  the list rather than extracting a shared util this round; small enough that
 *  a one-time copy beats the indirection. */
const COUNTRY_OPTIONS: string[] = [
  "Malta",
  "Gibraltar",
  "Isle of Man",
  "Curaçao",
  "United Kingdom",
  "Estonia",
  "Sweden",
  "Germany",
  "Netherlands",
  "Spain",
  "Italy",
  "France",
  "Cyprus",
  "Israel",
  "United States",
  "Canada",
  "Brazil",
  "Mexico",
  "Argentina",
  "Peru",
  "Chile",
  "Colombia",
  "India",
  "Philippines",
  "Singapore",
  "Hong Kong",
  "Australia",
  "New Zealand",
  "South Africa",
  "Nigeria",
  "United Arab Emirates",
  "Turkey",
  "Switzerland",
  "Ireland",
  "Denmark",
  "Finland",
  "Norway",
  "Other",
];

/** Slot times in UTC. Picked to span EU mornings, EU/US-East midday, and
 *  US-West afternoons so a visitor in any of our three biggest operator
 *  timezones (CET/EST/PST) can find at least one slot inside business hours. */
const SLOT_HOURS: number[] = [10, 13, 15, 17];
const DAYS_AHEAD = 14;
const MIN_SLOTS = 1;
const MAX_SLOTS = 5;

interface SlotOption {
  iso: string;
  /** Human-readable label like "Tue Jun 4 · 13:00 UTC". */
  label: string;
  /** Bucket key for the day so we can group buttons under a date heading. */
  dayKey: string;
  /** Heading for the day column. */
  dayLabel: string;
}

const DAY_FORMATTER = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

/** Build the next `DAYS_AHEAD` weekdays × `SLOT_HOURS.length` slots, starting
 *  from tomorrow UTC. We skip Saturday/Sunday so the founder's calendar
 *  doesn't fill up with weekend asks. Returns the flat list of options + a
 *  Map keyed by dayKey so the UI can render columns without an extra pass. */
function buildSlotOptions(
  now: Date,
): { all: SlotOption[]; byDay: Map<string, SlotOption[]> } {
  const all: SlotOption[] = [];
  const byDay = new Map<string, SlotOption[]>();
  // Start at tomorrow UTC midnight so we never offer a slot already in the past.
  const start = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1),
  );
  let added = 0;
  let dayOffset = 0;
  while (added < DAYS_AHEAD) {
    const day = new Date(start);
    day.setUTCDate(start.getUTCDate() + dayOffset);
    dayOffset += 1;
    const dow = day.getUTCDay();
    if (dow === 0 || dow === 6) continue; // skip weekends
    const dayLabel = DAY_FORMATTER.format(day);
    const dayKey = day.toISOString().slice(0, 10);
    const bucket: SlotOption[] = [];
    for (const hour of SLOT_HOURS) {
      const slot = new Date(day);
      slot.setUTCHours(hour, 0, 0, 0);
      const iso = slot.toISOString();
      const label = `${dayLabel} · ${String(hour).padStart(2, "0")}:00 UTC`;
      const opt: SlotOption = { iso, label, dayKey, dayLabel };
      bucket.push(opt);
      all.push(opt);
    }
    byDay.set(dayKey, bucket);
    added += 1;
  }
  return { all, byDay };
}

type Status = "idle" | "submitting" | "success" | "error" | "server-error";

interface FormState {
  operator: string;
  name: string;
  email: string;
  country: string;
  topic: BookingTopic;
  message: string;
  /** ISO strings of the slots the visitor toggled on. */
  selectedSlots: string[];
  /** Honeypot. */
  website: string;
}

const EMPTY: FormState = {
  operator: "",
  name: "",
  email: "",
  country: "",
  topic: "sandbox-walkthrough",
  message: "",
  selectedSlots: [],
  website: "",
};

const PAGE_LD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Book a 30-min intro · Vault Breaker Studios",
  url: "https://vaultbreaker.dev/book",
  description:
    "Pick three preferred slots for a 30-minute intro call with Vault Breaker Studios. We confirm one within 1 business day.",
} as const;

export function Book() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  // Memoize so we don't rebuild the slot grid on every keystroke. Recomputed
  // once per mount — fine for a session-long booking flow.
  const { byDay } = useMemo(() => buildSlotOptions(new Date()), []);

  const onChange =
    <K extends keyof FormState>(key: K) =>
    (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
    ) => {
      setForm((f) => ({ ...f, [key]: e.target.value as FormState[K] }));
    };

  const toggleSlot = (iso: string) => {
    setForm((f) => {
      const has = f.selectedSlots.includes(iso);
      if (has) {
        return { ...f, selectedSlots: f.selectedSlots.filter((s) => s !== iso) };
      }
      if (f.selectedSlots.length >= MAX_SLOTS) {
        // Silently ignore further clicks; the helper-text already explains
        // the cap so we don't need to surface an error toast here.
        return f;
      }
      return { ...f, selectedSlots: [...f.selectedSlots, iso] };
    });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (form.selectedSlots.length < MIN_SLOTS) {
      setError(`Pick at least ${MIN_SLOTS} time slot.`);
      return;
    }

    // Pull UTM/referrer attribution dynamically — same try/catch pattern
    // Contact.tsx uses, so a missing/broken lib doesn't take down booking.
    let utm: Attribution | undefined;
    try {
      const mod = (await import("../lib/utm.js")) as {
        getAttribution?: () => Attribution;
      };
      utm = mod.getAttribution?.();
    } catch {
      utm = undefined;
    }

    const payload = {
      operator: form.operator,
      name: form.name,
      email: form.email,
      country: form.country,
      topic: form.topic,
      timeSlots: form.selectedSlots,
      message: form.message.trim() ? form.message : undefined,
      website: form.website,
      ...(utm ? { utm } : {}),
    };

    const parsed = BookingRequest.safeParse(payload);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      setError(first ? `${first.path.join(".")}: ${first.message}` : "Invalid submission");
      return;
    }

    setStatus("submitting");
    try {
      const res = await fetch(BOOKING_ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(parsed.data),
      });
      if (!res.ok) {
        if (res.status === 429) {
          setStatus("error");
          setError("Too many booking requests — try again in an hour.");
          return;
        }
        if (res.status >= 500) {
          setStatus("server-error");
          return;
        }
        setStatus("error");
        setError(`Submission rejected (${res.status}).`);
        return;
      }
      setStatus("success");
    } catch (err) {
      setStatus("server-error");
      setError(err instanceof Error ? err.message : "Network error");
    }
  };

  if (status === "success") {
    return (
      <>
        <Seo title="Booking received · Vault Breaker Studios" path="/book" />
        <Section style={{ paddingTop: 120, paddingBottom: 120, maxWidth: 720 }}>
          <Badge kind="fair" style={{ marginBottom: 18 }}>
            RECEIVED
          </Badge>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(36px, 5vw, 56px)",
              lineHeight: 1.05,
              margin: 0,
              textTransform: "uppercase",
            }}
          >
            Got it. We'll confirm one of your slots within 1 business day.
          </h1>
        </Section>
      </>
    );
  }

  if (status === "server-error") {
    return (
      <>
        <Seo title="Something broke · Vault Breaker Studios" path="/book" />
        <Section style={{ paddingTop: 120, paddingBottom: 120, maxWidth: 720 }}>
          <Badge kind="bust" style={{ marginBottom: 18 }}>
            SERVER HICCUP
          </Badge>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(32px, 4.5vw, 48px)",
              lineHeight: 1.05,
              margin: 0,
              textTransform: "uppercase",
            }}
          >
            We couldn't take that booking.
          </h1>
          <p style={{ fontSize: 16, color: "var(--fg-2)", marginTop: 16 }}>
            The form server is being a diva. Email us and we'll book the call by hand —
            tell us your preferred times in the body.
          </p>
          <Card style={{ padding: 22, marginTop: 24, display: "grid", gap: 14 }}>
            <div style={{ fontSize: 14, color: "var(--fg-2)" }}>
              <strong style={{ color: "var(--fg-1)" }}>Email us directly</strong>
              <br />
              <a
                href={buildBookingMailto(form)}
                style={{ color: "var(--cyan)" }}
              >
                hello@vaultbreaker.dev
              </a>
            </div>
            <a
              href={buildBookingMailto(form)}
              style={{ textDecoration: "none", justifySelf: "start" }}
            >
              <Button variant="gold" style={{ padding: "14px 22px", fontSize: 15 }}>
                Open email with my slots ↗
              </Button>
            </a>
            {error ? (
              <span
                style={{
                  fontSize: 12,
                  color: "var(--fg-3)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                {error}
              </span>
            ) : null}
          </Card>
          <div style={{ marginTop: 18 }}>
            <Button
              variant="ghost"
              onClick={() => setStatus("idle")}
              style={{ padding: "12px 18px", fontSize: 14 }}
            >
              ← Try the form again
            </Button>
          </div>
        </Section>
      </>
    );
  }

  const selectedCount = form.selectedSlots.length;

  return (
    <>
      <Seo
        title="Book a call · Vault Breaker Studios"
        description="Book a 30-minute intro with Vault Breaker Studios. Pick three preferred slots and we confirm one within 1 business day."
        path="/book"
        jsonLd={PAGE_LD}
      />

      <Section>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          BOOK A CALL
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(44px, 6vw, 72px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          Book a 30-min intro.
        </h1>
        <p style={{ fontSize: 17, color: "var(--fg-2)", marginTop: 14, maxWidth: 720, lineHeight: 1.55 }}>
          Pick three slots that work — we confirm one within 1 business day. Or skip
          the call and{" "}
          <Link to="/contact" style={{ color: "var(--cyan)" }}>
            send us a message
          </Link>{" "}
          instead.
        </p>
      </Section>

      <Section style={{ paddingTop: 0, maxWidth: 880 }}>
        <Card style={{ padding: 28 }}>
          <form onSubmit={onSubmit} style={{ display: "grid", gap: 20 }}>
            <Row>
              <Field label="Operator / brand" required>
                <input
                  value={form.operator}
                  onChange={onChange("operator")}
                  className="vb-text-input"
                  required
                  minLength={2}
                />
              </Field>
              <Field label="Your name" required>
                <input
                  value={form.name}
                  onChange={onChange("name")}
                  className="vb-text-input"
                  required
                  minLength={2}
                />
              </Field>
            </Row>

            <Row>
              <Field label="Work email" required>
                <input
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  className="vb-text-input"
                  required
                />
              </Field>
              <Field label="Country" required>
                <select
                  value={form.country}
                  onChange={onChange("country")}
                  className="vb-text-input"
                  required
                >
                  <option value="" disabled>
                    Select a jurisdiction…
                  </option>
                  {COUNTRY_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </Field>
            </Row>

            <Field label="Topic" required>
              <select
                value={form.topic}
                onChange={onChange("topic")}
                className="vb-text-input"
                required
              >
                {TOPIC_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>

            <div style={{ display: "grid", gap: 10 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <span className="label" style={{ color: "var(--fg-2)" }}>
                  Preferred time slots <span style={{ color: "var(--red)" }}>*</span>
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color:
                      selectedCount >= MAX_SLOTS ? "var(--gold)" : "var(--fg-3)",
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  {selectedCount}/{MAX_SLOTS} selected · times in UTC
                </span>
              </div>
              <p style={{ fontSize: 13, color: "var(--fg-3)", margin: 0 }}>
                Click between {MIN_SLOTS} and {MAX_SLOTS} slots — we'll confirm one of
                them. Weekends are excluded; all times are UTC.
              </p>
              <SlotGrid
                byDay={byDay}
                selected={form.selectedSlots}
                onToggle={toggleSlot}
                disabled={status === "submitting"}
              />
            </div>

            <Field label="Anything we should know? (optional)">
              <textarea
                value={form.message}
                onChange={onChange("message")}
                className="vb-text-input"
                rows={4}
                maxLength={2000}
                placeholder="What you'd like to cover, current aggregators, anything that helps us prep."
                style={{ resize: "vertical", minHeight: 96 }}
              />
            </Field>

            {/* Honeypot — hidden for humans, filled by bots */}
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={onChange("website")}
              tabIndex={-1}
              autoComplete="off"
              style={{
                position: "absolute",
                left: "-10000px",
                width: 1,
                height: 1,
                opacity: 0,
              }}
              aria-hidden
            />

            {error ? (
              <div
                style={{
                  padding: "12px 14px",
                  border: "1px solid rgba(255,59,71,0.35)",
                  background: "var(--red-wash)",
                  color: "var(--red)",
                  fontSize: 13,
                  borderRadius: 10,
                }}
              >
                {error}
              </div>
            ) : null}

            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: "var(--fg-3)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                We confirm exactly one slot — no double-bookings.
              </span>
              <Button
                variant="gold"
                type="submit"
                disabled={
                  status === "submitting" || form.selectedSlots.length < MIN_SLOTS
                }
                style={{ padding: "14px 22px", fontSize: 15 }}
              >
                {status === "submitting" ? "SENDING…" : "REQUEST BOOKING"}
              </Button>
            </div>
          </form>
        </Card>
      </Section>

      <Section style={{ paddingTop: 0, maxWidth: 880 }}>
        <SectionHeading
          eyebrow="Other channels"
          title="Or skip the call"
        />
        <Card style={{ display: "grid", gap: 10, padding: 22 }}>
          <span className="label" style={{ color: "var(--fg-2)" }}>
            Async works too
          </span>
          <a
            href="mailto:hello@vaultbreaker.dev"
            style={{ color: "var(--fg-1)", fontSize: 18, fontWeight: 600 }}
          >
            hello@vaultbreaker.dev
          </a>
          <span style={{ fontSize: 12, color: "var(--fg-3)" }}>
            Or use the{" "}
            <Link to="/contact" style={{ color: "var(--cyan)" }}>
              contact form
            </Link>
            {" "}— we reply within 1 business day either way.
          </span>
        </Card>
      </Section>
    </>
  );
}

/** Group day buckets into columns so the grid stays scannable on wide screens
 *  and stacks gracefully on mobile via `grid-template-columns: auto-fit`. */
function SlotGrid({
  byDay,
  selected,
  onToggle,
  disabled,
}: {
  byDay: Map<string, SlotOption[]>;
  selected: string[];
  onToggle: (iso: string) => void;
  disabled: boolean;
}) {
  const days = Array.from(byDay.entries());
  return (
    <div
      style={{
        display: "grid",
        gap: 12,
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      }}
    >
      {days.map(([dayKey, slots]) => {
        const heading = slots[0]?.dayLabel ?? dayKey;
        return (
          <div
            key={dayKey}
            style={{
              border: "1px solid var(--line)",
              borderRadius: 10,
              padding: "12px 12px 14px",
              background: "rgba(0,0,0,0.18)",
              display: "grid",
              gap: 8,
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "var(--fg-3)",
                letterSpacing: 0.4,
                textTransform: "uppercase",
              }}
            >
              {heading}
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              {slots.map((slot) => {
                const isSelected = selected.includes(slot.iso);
                const atCap = !isSelected && selected.length >= MAX_SLOTS;
                const hour = slot.iso.slice(11, 16);
                return (
                  <button
                    key={slot.iso}
                    type="button"
                    onClick={() => onToggle(slot.iso)}
                    disabled={disabled || atCap}
                    aria-pressed={isSelected}
                    aria-label={slot.label}
                    style={{
                      cursor: disabled || atCap ? "not-allowed" : "pointer",
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: isSelected
                        ? "1px solid var(--gold)"
                        : "1px solid var(--line)",
                      background: isSelected
                        ? "rgba(255, 184, 0, 0.18)"
                        : "rgba(255,255,255,0.02)",
                      color: isSelected ? "var(--gold)" : "var(--fg-1)",
                      fontFamily: "var(--font-mono)",
                      fontSize: 13,
                      letterSpacing: 0.3,
                      opacity: atCap ? 0.45 : 1,
                      transition: "background 120ms, border-color 120ms",
                    }}
                  >
                    {hour} UTC
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Build a `mailto:` URL pre-filled with the visitor's slot picks so the 5xx
 *  fallback CTA drops them into their email client without losing input. */
function buildBookingMailto(form: FormState): string {
  const operator = form.operator.trim() || "your studio";
  const subject = `Booking request from ${operator}`;
  const slotsLine =
    form.selectedSlots.length > 0
      ? form.selectedSlots.map((s) => `  • ${s}`).join("\n")
      : "  • (no slots picked)";
  const bodyLines = [
    form.message.trim() || "(no extra notes)",
    "",
    "—",
    `Operator: ${form.operator}`,
    `Name: ${form.name}`,
    `Email: ${form.email}`,
    `Country: ${form.country}`,
    `Topic: ${form.topic}`,
    `Preferred slots (UTC):`,
    slotsLine,
  ]
    .filter(Boolean)
    .join("\n");
  return `mailto:hello@vaultbreaker.dev?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines)}`;
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gap: 16,
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "grid", gap: 6 }}>
      <span className="label" style={{ color: "var(--fg-2)" }}>
        {label}
        {required ? <span style={{ color: "var(--red)" }}> *</span> : null}
      </span>
      {children}
    </label>
  );
}
